'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { ChatAttachmentFileIcon } from '@/components/chat-attachment-icon';
import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';
import type { MappedAttachment, MappedMessage } from '@/shared/mobile-service/lib/message-mapping';
import { CollectedMediaPreview } from '../media/CollectedMediaPreview';
import { useOpenCollectedMedia } from '../media/collected-media';
import { formatDateTime } from '../renderers';

/**
 * One chat bubble. Outgoing (owner-sent) messages align right with a blue fill;
 * incoming messages align left in gray. Clicking the body opens the full
 * artifact as markdown. Clicking an image or video opens the evidence viewer.
 *
 * A cited message gets a ring; context around it is dimmed so expanded
 * thread rows are never mistaken for what the assistant cited.
 *
 * File attachments render above the body: images as thumbnails, video and
 * audio as compact players, and everything else as a file icon plus name.
 */
export function MessageBubble({
  message,
  focused = false,
  dimmed = false,
  onSelect,
}: {
  message: MappedMessage;
  focused?: boolean;
  dimmed?: boolean;
  onSelect: (message: MappedMessage) => void;
}) {
  const t = useTimePilotT();
  const { open, resolvePreview, resolvePreviewInfo } = useOpenCollectedMedia();
  const outgoing = message.direction === 'outgoing';
  const focusRing = focused
    ? outgoing
      ? 'ring-2 ring-offset-2 ring-offset-[#0b1220] ring-blue-300'
      : 'ring-2 ring-offset-2 ring-offset-[#0b1220] ring-amber-400'
    : '';
  const playable = message.attachments.filter(
    (attachment) => attachment.kind === 'video' || attachment.kind === 'audio',
  );
  const inline = message.attachments.filter(
    (attachment) => attachment.kind !== 'video' && attachment.kind !== 'audio',
  );
  const showEmptyBody = !message.body && message.attachments.length === 0;

  return (
    <div
      data-message=""
      data-focused={focused ? 'true' : undefined}
      className={`flex ${outgoing ? 'justify-end' : 'justify-start'} ${dimmed ? 'opacity-45' : ''}`}
    >
      <div className={`flex max-w-[75%] flex-col ${outgoing ? 'items-end' : 'items-start'}`}>
        {!outgoing && message.senderLabel && (
          <span className="mb-0.5 px-1 text-xs font-medium text-white/65">
            {message.senderLabel}
          </span>
        )}
        <div
          onClick={() => onSelect(message)}
          className={`flex flex-col gap-1.5 rounded-2xl px-3 py-2 text-left text-sm shadow-sm ${focusRing} ${
            outgoing
              ? 'rounded-br-sm bg-brand-blue text-white'
              : 'rounded-bl-sm bg-white/10 text-white'
          }`}
        >
          {playable.map((attachment) =>
            attachment.kind === 'video' ? (
              <CollectedMediaPreview
                key={attachment.url}
                url={attachment.url}
                kind="video"
                filename={attachment.filename}
                resolvePreview={resolvePreview}
                resolvePreviewInfo={resolvePreviewInfo}
                onOpen={(previewUrl) =>
                  open({
                    url: attachment.url,
                    kind: 'video',
                    filename: attachment.filename,
                    previewUrl,
                  })
                }
              />
            ) : (
              <PlayablePreview key={attachment.url} attachment={attachment} />
            ),
          )}
          {inline
            .filter((attachment) => attachment.kind === 'image')
            .map((attachment) => (
              <CollectedMediaPreview
                key={attachment.url}
                url={attachment.url}
                kind="image"
                filename={attachment.filename}
                resolvePreview={resolvePreview}
                resolvePreviewInfo={resolvePreviewInfo}
                onOpen={(previewUrl) =>
                  open({
                    url: attachment.url,
                    kind: 'image',
                    filename: attachment.filename,
                    previewUrl,
                  })
                }
              />
            ))}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(message);
            }}
            className="flex flex-col gap-1.5 text-left transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-blue-light"
          >
            {inline
              .filter((attachment) => attachment.kind !== 'image')
              .map((attachment) => (
                <FilePreview key={attachment.url} attachment={attachment} />
              ))}
            {message.body ? (
              <span className="whitespace-pre-wrap break-words">{message.body}</span>
            ) : showEmptyBody ? (
              <span className="italic opacity-70">(no text)</span>
            ) : inline.length === 0 ? (
              <span className="sr-only">{t("viewers.viewMessageDetails")}</span>
            ) : null}
          </button>
        </div>
        {message.timestamp && (
          <span className="mt-0.5 px-1 text-[11px] tabular-nums text-white/50">
            {formatDateTime(message.timestamp)}
          </span>
        )}
      </div>
    </div>
  );
}

function PlayablePreview({ attachment }: { attachment: MappedAttachment }) {
  const src = storageUrlForDisplay(attachment.url);
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();
  if (attachment.kind === 'audio') {
    return (
      <audio
        controls
        preload="metadata"
        src={src}
        aria-label={attachment.filename ?? 'Attached audio'}
        className="w-full min-w-48"
        onClick={stop}
      />
    );
  }
  return (
    <video
      controls
      playsInline
      preload="metadata"
      src={src}
      aria-label={attachment.filename ?? 'Attached video'}
      className="max-h-64 w-full rounded-lg bg-black"
      onClick={stop}
    />
  );
}

function FilePreview({ attachment }: { attachment: MappedAttachment }) {
  const name = attachment.filename ?? 'Attachment';
  return (
    <span className="flex min-w-0 items-center gap-2">
      <ChatAttachmentFileIcon filename={attachment.filename ?? attachment.url} size={18} />
      <span className="truncate text-xs" title={name}>
        {name}
      </span>
    </span>
  );
}
